import { useEffect, useRef, useState } from 'react';
import IconButton from '../common/IconButton';
import { TFunction } from 'i18next';
import { formatCampaignDate } from '@/utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { notificationActions } from '@/config/notificationActions';
import copyToClipboard from '@/utils/clipboardUtils';
import { useNotificationContext } from '@/context/NotificationContext';
import {
  useNotificationsQuery,
  useMarkAllNotificationsRead,
  useMarkSingleNotificationRead,
} from '../../hooks/queries/useNotificationQueries';
const formatNotificationTitle = (text: string) => {
  return text.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};
type Notification = {
  _id: string;
  message: string;
  createdAt: string;
  isRead?: boolean;
  notificationType: string;
  category?: string;
  params?: Record<string, any>;
};
interface Props {
  t: TFunction<'translation', undefined>;
  userId?: string;
}

const NotificationDropdown = ({ t, userId }: Props) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const isRedirecting = useRef(false);
  const hasSingleRead = useRef(false);
  const {
    hasUnread,
    markAllAsRead: contextMarkAllAsRead,
    setHasUnread,
  } = useNotificationContext();

  const { data: notifications = [], refetch } = useNotificationsQuery(
    userId || '',
  );
  const markAllMutation = useMarkAllNotificationsRead(userId || '');
  const markSingleMutation = useMarkSingleNotificationRead(userId || '');

  useEffect(() => {
    if (notifications.length > 0) {
      setHasUnread(notifications.some((n: Notification) => !n.isRead));
    }
  }, [notifications]);

  const markAllAsRead = async () => {
    if (!userId) return;
    try {
      await markAllMutation.mutateAsync();
      setHasUnread(false);
    } catch (error) {
      console.error('Error marking notifications as read', error);
    }
  };

  const markSingleAsRead = async (notificationId: string) => {
    try {
      await markSingleMutation.mutateAsync(notificationId);
      const updated = (notifications as Notification[]).map((n) =>
        n._id === notificationId ? { ...n, isRead: true } : n,
      );
      setHasUnread(updated.some((n) => !n.isRead));
    } catch (error) {
      console.error('Error marking notification as read', error);
    }
  };
  const handleNotificationRedirect = async (
    notificationId: string,
    redirect?: string,
    state?: Record<string, any>,
  ) => {
    if (!redirect) return;
    isRedirecting.current = true;
    hasSingleRead.current = true;
    await markSingleAsRead(notificationId);

    setIsOpen(false);

    if (state) {
      navigate(redirect, { state });
    } else {
      navigate(redirect);
    }
  };

  const handleSingleNotificationClick = async (notificationId: string) => {
    hasSingleRead.current = true;
    await markSingleAsRead(notificationId);
  };

  useEffect(() => {
    const handleClickOutside = async (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        if (!isRedirecting.current && !hasSingleRead.current) {
          await markAllAsRead();
          contextMarkAllAsRead();
        }
        setIsOpen(false);
        isRedirecting.current = false;
        hasSingleRead.current = false;
      }
    };

    if (isOpen) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) {
      const formattedDate = formatCampaignDate(dateString);

      const formattedTime = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });

      return `${formattedDate} ${formattedTime}`;
    }
    return formatCampaignDate(dateString);
  };

  return (
    <div
      ref={modalRef}
      className={`relative ${isOpen ? 'notification-open' : ''}`}
    >
      <div className="relative">
        <IconButton
          src="/assets/svgs/fi_bell.svg"
          tooltip={String(t('common.notification'))}
          onClick={async () => {
            if (isOpen) {
              if (!isRedirecting.current) {
                await markAllAsRead();
                contextMarkAllAsRead();
              }
            } else {
              refetch();
              hasSingleRead.current = false;
            }

            setIsOpen((p) => !p);
          }}
        />
        {hasUnread && (
          <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full"></span>
        )}
      </div>

      {isOpen && (
        <div className="fixed right-3 top-[63px] w-[380px] max-h-[94vh] bg-white border shadow-lg z-50 flex flex-col">
          {notifications.length === 0 && (
            <div className="px-4 py-2 text-sm text-gray-500">
              {t('common.noNotifications')}
            </div>
          )}

          <div
            className="flex-1 overflow-y-auto mr-2
  [&::-webkit-scrollbar]:w-[9px]
  [&::-webkit-scrollbar-track]:bg-transparent
  [&::-webkit-scrollbar-thumb]:bg-[#B3B3B2]
  [&::-webkit-scrollbar-thumb]:rounded-full
  [&::-webkit-scrollbar-thumb]:border-[2px]
  [&::-webkit-scrollbar-thumb]:border-transparent
  [&::-webkit-scrollbar-thumb]:bg-clip-content"
          >
            {(notifications as Notification[]).map((n) => {
              const action = notificationActions[n.notificationType];
              let redirectPath: string | undefined;
              let actionState: Record<string, any> | undefined;

              if (typeof action?.redirect === 'function') {
                redirectPath = action.redirect(n);
              } else {
                redirectPath = action?.redirect;
              }
              if (typeof action?.state === 'function') {
                actionState = action.state(n);
              } else {
                actionState = action?.state;
              }

              return (
                <div
                  key={n._id}
                  className={`px-4 py-3 ${!n.isRead ? 'bg-[#F0F5FF]' : ''}`}
                >
                  <div
                    className={`font-semibold text-base ${
                      redirectPath ? 'cursor-pointer' : ''
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();

                      if (
                        n.notificationType === 'smartlink_is_live' &&
                        n.params?.link
                      ) {
                        const link = n.params.link.subDomain
                          ? `https://${n.params.link.subDomain ?? ''}.${n.params.link.domain ?? 'linkfluencer.io'}/${n.params.link.shortUrl}`
                          : `https://${n.params.link.domain ?? 'linkfluencer.io'}/${n.params.link.shortUrl}`;

                        copyToClipboard(
                          link,
                          t('toast.linkCopied'),
                          t('toast.linkCopiedError'),
                        );
                      } else if (action?.redirect) {
                        handleNotificationRedirect(
                          n._id,
                          redirectPath,
                          actionState,
                        );
                      } else if (!n.isRead) {
                        handleSingleNotificationClick(n._id);
                      }
                    }}
                  >
                    {formatNotificationTitle(n.notificationType)}
                  </div>
                  <div
                    className={`text-sm ${redirectPath ? 'cursor-pointer' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();

                      if (
                        n.notificationType === 'smartlink_is_live' &&
                        n.params?.link
                      ) {
                        const link = n.params.link.subDomain
                          ? `https://${n.params.link.subDomain ?? ''}.${n.params.link.domain ?? 'linkfluencer.io'}/${n.params.link.shortUrl}`
                          : `https://${n.params.link.domain ?? 'linkfluencer.io'}/${n.params.link.shortUrl}`;

                        copyToClipboard(
                          link,
                          t('toast.linkCopied'),
                          t('toast.linkCopiedError'),
                        );
                      } else if (action?.redirect) {
                        handleNotificationRedirect(
                          n._id,
                          redirectPath,
                          actionState,
                        );
                      } else if (!n.isRead) {
                        handleSingleNotificationClick(n._id);
                      }
                    }}
                  >
                    {n.message}
                  </div>

                  {action?.cta && action?.redirect && (
                    <div
                      className={`text-[14px] font-[600] underline cursor-pointer mt-1 mb-2 ${
                        n.isRead ? 'text-[#a5acb8]' : 'text-[#3C2479]'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          n.notificationType === 'smartlink_is_live' &&
                          n.params?.link
                        ) {
                          const link = n.params.link.subDomain
                            ? `https://${n.params.link.subDomain ?? ''}.${n.params.link.domain ?? 'linkfluencer.io'}/${n.params.link.shortUrl}`
                            : `https://${n.params.link.domain ?? 'linkfluencer.io'}/${n.params.link.shortUrl}`;

                          copyToClipboard(
                            link,
                            t('toast.linkCopied'),
                            t('toast.linkCopiedError'),
                          );

                          return;
                        }

                        handleNotificationRedirect(
                          n._id,
                          redirectPath,
                          actionState,
                        );
                      }}
                    >
                      {action.cta}
                    </div>
                  )}
                  <div className="text-xs text-[#a5acb8]">
                    {formatDate(n.createdAt)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
